import React, { useState, useEffect } from "react";
import { FiSearch, FiX } from "../assets/icons/vander";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { fetchfilter } from "../ApiService/filterapi/filterapi";

export default function Filter({
  selectedCategories,
  setSelectedCategories,
  selectedBrands,
  setSelectedBrands,
  selectedColors,
  setSelectedColors,
  selectedSizes,
  setSelectedSizes,
  searchTerm,
  setSearchTerm,
  onClose // Add this prop for mobile close button
}) {
  const [openSection, setOpenSection] = useState(null);
  const [brandSearch, setBrandSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [sizeSearch, setSizeSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Toggle open/close section
  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Toggle selection for IDs/codes
  const toggleSelection = (value, setFn, selected, key = null) => {
    const exists = key ? selected.some(item => item[key] === value[key]) : selected.includes(value);
    if (exists) {
      setFn(selected.filter(item => (key ? item[key] !== value[key] : item !== value)));
    } else {
      setFn([...selected, value]);
    }
  };

  // Fetch filter data (categories, brands, variants)
  useEffect(() => {
    const getFilterData = async () => {
      try {
        setLoading(true);
        const data = await fetchfilter();
        setCategories(data.categories || []);
        setBrands(data.brands || []);

        const colorVariant = data.variants?.find(v => v.variant_name.toLowerCase() === "color");
        const sizeVariant = data.variants?.find(v => v.variant_name.toLowerCase() === "sizes");

        setColors(colorVariant?.variant_data || []);
        setSizes(sizeVariant?.variant_data || []);
      } catch (error) {
        console.error("Failed to fetch filter data:", error);
      } finally {
        setLoading(false);
      }
    };
    getFilterData();
  }, []);

  // Restore filters from localStorage on mount
  useEffect(() => {
    const savedFilters = JSON.parse(localStorage.getItem("shopFilters"));
    if (savedFilters) {
      setSelectedCategories(savedFilters.selectedCategories || []);
      setSelectedBrands(savedFilters.selectedBrands || []);
      setSelectedColors(savedFilters.selectedColors || []);
      setSelectedSizes(savedFilters.selectedSizes || []);
    }
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filters = {
      selectedCategories,
      selectedBrands,
      selectedColors,
      selectedSizes
    };
    localStorage.setItem("shopFilters", JSON.stringify(filters));
  }, [selectedCategories, selectedBrands, selectedColors, selectedSizes]);

  // Filter search inputs
  const filteredBrands = brands.filter(b => b.brand_name.toLowerCase().includes(brandSearch.toLowerCase()));
  const filteredCategories = categories.filter(c => c.category_name.toLowerCase().includes(categorySearch.toLowerCase()));
  const filteredSizes = sizes.filter(s => s.name.toLowerCase().includes(sizeSearch.toLowerCase()));

  if (loading) return <p className="text-center text-gray-400 py-10">Loading filters...</p>;

  // Clear all selected filters
  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedColors([]);
    setSelectedSizes([]);
    localStorage.removeItem("shopFilters"); // remove saved filters
  };

  return (
    <div className="lg:col-span-3 md:col-span-4">
      <div className="rounded shadow dark:shadow-gray-800 p-4 sticky top-20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <h5 className="text-xl font-medium mb-2 sm:mb-0 text-center sm:text-left">Filters</h5>
          
          {/* Mobile close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="md:hidden text-gray-500 hover:text-gray-700 self-end"
            >
              <FiX className="h-6 w-6" />
            </button>
          )}
          
          {(selectedCategories.length > 0 ||
            selectedBrands.length > 0 ||
            selectedColors.length > 0 ||
            selectedSizes.length > 0) && (
              <button
                onClick={clearAllFilters}
                className="bg-red-500 text-white text-sm px-2 py-1 rounded hover:bg-red-600 transition-all duration-200 self-center sm:self-auto"
              >
                Clear All Filters
              </button>
            )}
        </div>

        {/* Product Search */}
        <div>
          <label className="font-medium">Search Products:</label>
          <div className="relative mt-2">
            <FiSearch className="absolute size-4 top-[9px] end-4 text-slate-900 dark:text-white" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border rounded p-2 w-full"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="mt-4 border-b pb-2">
          <button className="flex justify-between w-full font-medium" onClick={() => toggleSection("categories")}>
            Categories
            {openSection === "categories" ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {openSection === "categories" && (
            <>
              <input
                type="text"
                placeholder="Search Category"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="w-full h-8 px-2 border rounded mt-2 text-sm"
              />
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {filteredCategories.map((cat, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`cat-${index}`}
                      checked={selectedCategories.some(c => c.category_id === cat.category_id)}
                      onChange={() => toggleSelection(cat, setSelectedCategories, selectedCategories, 'category_id')}
                    />
                    <label htmlFor={`cat-${index}`} className="capitalize">{cat.category_name}</label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Colors */}
        <div className="mt-4 border-b pb-2">
          <button className="flex justify-between w-full font-medium" onClick={() => toggleSection("colors")}>
            Colors
            {openSection === "colors" ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {openSection === "colors" && (
            <ul className="mt-3 flex flex-wrap gap-3">
              {colors.map((color, index) => {
                const isSelected = selectedColors.some(c => c.code === color.code);
                return (
                  <li key={index}>
                    <div
                      onClick={() => toggleSelection(color, setSelectedColors, selectedColors, 'code')}
                      className={`relative w-8 h-8 rounded-full cursor-pointer flex items-center justify-center border-2 transition-all duration-200`}
                      style={{ borderColor: color.code, backgroundColor: color.code }}
                      title={color.name}
                    >
                      {isSelected && <span className="absolute text-white text-lg font-bold">✔</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Brands */}
        <div className="mt-4 border-b pb-2">
          <button className="flex justify-between w-full font-medium" onClick={() => toggleSection("brands")}>
            Brands
            {openSection === "brands" ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {openSection === "brands" && (
            <>
              <input
                type="text"
                placeholder="Search Brand"
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                className="w-full h-8 px-2 border rounded mt-2 text-sm"
              />
              <ul className="mt-2 max-h-40 overflow-y-auto space-y-1">
                {filteredBrands.map((brand, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`brand-${index}`}
                      checked={selectedBrands.some(b => b.brand_id === brand.brand_id)}
                      onChange={() => toggleSelection(brand, setSelectedBrands, selectedBrands, 'brand_id')}
                    />
                    <label htmlFor={`brand-${index}`} className="capitalize">{brand.brand_name}</label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Sizes */}
        <div className="mt-4 border-b pb-2">
          <button className="flex justify-between w-full font-medium" onClick={() => toggleSection("sizes")}>
            Sizes
            {openSection === "sizes" ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          {openSection === "sizes" && (
            <>
              <input
                type="text"
                placeholder="Search Size"
                value={sizeSearch}
                onChange={(e) => setSizeSearch(e.target.value)}
                className="w-full h-8 px-2 border rounded mt-2 text-sm"
              />
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {filteredSizes.map((size, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`size-${index}`}
                      checked={selectedSizes.some(s => s.code === size.code)}
                      onChange={() => toggleSelection(size, setSelectedSizes, selectedSizes, 'code')}
                    />
                    <label htmlFor={`size-${index}`} className="uppercase">{size.name}</label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Selected Filters */}
        {/* Show Selected Filters only when something is chosen */}
        {(selectedCategories.length > 0 ||
          selectedBrands.length > 0 ||
          selectedColors.length > 0 ||
          selectedSizes.length > 0) && (
            <div className="mt-6 pt-3 text-sm text-slate-600 dark:text-slate-300 border-t">
              <h6 className="font-medium">Selected Filters:</h6>
              {selectedCategories.length > 0 && (
                <p>Categories: {selectedCategories.map(c => c.category_name).join(", ")}</p>
              )}
              {selectedBrands.length > 0 && (
                <p>Brands: {selectedBrands.map(b => b.brand_name).join(", ")}</p>
              )}
              {selectedColors.length > 0 && (
                <p>Colors: {selectedColors.map(c => c.name).join(", ")}</p>
              )}
              {selectedSizes.length > 0 && (
                <p>Sizes: {selectedSizes.map(s => s.name).join(", ")}</p>
              )}
            </div>
          )}
      </div>
    </div>
  );
}